import React from 'react';
import '../App.css';
import './Dashboard.css';

import MainPageHeader from './components/MainPageHeader';
import StatsGrid from './components/StatsGrid';
import DashboardActionButtons from './components/DashboardActionButtons';
import PlanetBackground from './components/PlanetBackground';
import PeriodType from './types/period.type';
import useHttp from '../../hooks/http.hook';
import { useNavigate, useParams } from 'react-router-dom';

function MainPage() {
  // View-only placeholders. Replace with real data later.
  const [branches, setBranches] = React.useState([]);
  const navigate = useNavigate();
  const params = useParams();
  const { requestWithMeta } = useHttp();
  // React 18 StrictMode runs effects twice in dev; dedupe in-flight requests
  // so we don't fire duplicate network calls and we still update state.
  const branchesPromiseRef = React.useRef(null);
  const lastRequestFnRef = React.useRef(null);

  const selectedBranchId =
    params.branchId && params.branchId !== 'store' ? params.branchId : null;

  const [periodId, setPeriodId] = React.useState(PeriodType.MONTH);

  const selectedBranch = React.useMemo(() => {
    if (!selectedBranchId) return null;
    // `useParams()` returns strings; API ids may be numbers.
    const selectedKey = String(selectedBranchId);
    return (
      branches.find((b) => {
        const id = b?._id ?? b?.id;
        if (id === undefined || id === null) return false;
        return String(id) === selectedKey;
      }) || null
    );
  }, [branches, selectedBranchId]);

  const selectedBranchName = selectedBranch?.name ?? null;
  const isBranchPickerDisabled = branches.length <= 1;
  const hasTextValue = React.useCallback((value) => {
    if (typeof value === 'string') return value.trim().length > 0;
    return value !== undefined && value !== null;
  }, []);

  const hasValidLocation = React.useCallback((location) => {
    if (!location || typeof location !== 'object') return false;
    const lat = Number(location?.lat);
    const lng = Number(location?.lng ?? location?.lon);
    return Number.isFinite(lat) && Number.isFinite(lng);
  }, []);

  const hasNonEmptySchedule = React.useCallback((branch) => {
    const schedule = branch?.schedule ?? branch?.chedule;
    return Array.isArray(schedule) && schedule.length > 0;
  }, []);

  React.useEffect(() => {
    let cancelled = false;

    if (lastRequestFnRef.current !== requestWithMeta) {
      lastRequestFnRef.current = requestWithMeta;
      branchesPromiseRef.current = null;
    }

    const branchesPromise =
      branchesPromiseRef.current ??
      (branchesPromiseRef.current = requestWithMeta('/operator/branches', 'GET'));

    (async () => {
      try {
        const branchesRes = await branchesPromise;

        if (cancelled) return;

        if (branchesRes?.status === 404) {
          navigate('/business/update', { replace: true });
          return;
        }

        if (branchesRes?.ok) {
          const data = branchesRes?.data;
          const listRaw = Array.isArray(data)
            ? data
            : Array.isArray(data?.branches)
              ? data.branches
              : [];
          const business = data?.business;

          // If base business profile is incomplete, force user to complete it first.
          if (!hasTextValue(business?.customName) || !hasTextValue(business?.logoUrl)) {
            navigate('/business/update', { replace: true });
            return;
          }

          const normalized = listRaw.map((b) => {
            const id = b?._id ?? b?.id;
            return { ...b, _id: id, id };
          });

          const firstInvalidBranch = normalized.find((branch) => {
            const missingLocation = !hasValidLocation(branch?.location);
            const missingSchedule = !hasNonEmptySchedule(branch);
            return missingLocation || missingSchedule;
          });

          if (firstInvalidBranch?._id || firstInvalidBranch?.id) {
            const invalidBranchId = firstInvalidBranch?._id ?? firstInvalidBranch?.id;
            navigate(`/business/branch/${invalidBranchId}/edit`, { replace: true });
            return;
          }

          setBranches(normalized);
        }
      } catch (_) {
        // ignore for now (view-first app)
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    requestWithMeta,
    navigate,
    hasTextValue,
    hasValidLocation,
    hasNonEmptySchedule,
  ]);

  React.useEffect(() => {
    // Navigation decisions based on already-fetched branches + current route.
    if (!Array.isArray(branches) || branches.length === 0) return;

    if (branches.length === 1) {
      const onlyId = branches[0]?._id ?? branches[0]?.id;
      if (!onlyId) return;
      if (String(params.branchId) === String(onlyId)) return;
      navigate(`/branch/${onlyId}`, { replace: true });
      return;
    }

    if (!params.branchId) {
      navigate('/branch/store', { replace: true });
    }
  }, [branches, params.branchId, navigate]);

  const rootRef = React.useRef(null);

  return (
    <div ref={rootRef} className="main-page-root main-page-root--planet">
      <PlanetBackground rootRef={rootRef} />
      <div className="main-page-mobile">
        <MainPageHeader
          branches={branches}
          selectedBranchName={selectedBranchName}
          isBranchPickerDisabled={isBranchPickerDisabled}
          onSelectBranchName={(nameOrNull) => {
            if (nameOrNull === null) {
              navigate('/branch/store');
              return;
            }
            const found = branches.find((b) => b?.name === nameOrNull);
            const id = found?._id ?? found?.id;
            if (id) navigate(`/branch/${id}`);
          }}
        />

        <main className="main-page-content">
          <StatsGrid periodId={periodId} />
          <DashboardActionButtons
            selectedBranchId={selectedBranchId}
            periodId={periodId}
            onPeriodChange={setPeriodId}
          />
        </main>
      </div>
    </div>
  );
}

export default MainPage;

